import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import {
  AdminUsersQuery,
  AdminUsersQueryVariables,
  DeactivateUserMutation,
  DeactivateUserMutationVariables,
  DeleteUserMutation,
  DeleteUserMutationVariables,
  GetAIStaffMembersQuery,
  GetAIStaffMembersQueryVariables,
  GetIntercomJwtQuery,
  GetIntercomJwtQueryVariables,
  GetOrganizationCalendarConnectionsQuery,
  GetOrganizationCalendarConnectionsQueryVariables,
  GetTestApplicantQuery,
  GetTestApplicantQueryVariables,
  GetUserByUuidQuery,
  GetUserByUuidQueryVariables,
  GetUsersByIdsQuery,
  GetUsersByIdsQueryVariables,
  GetUsersThatCanBeInputsForFieldQuery,
  GetUsersThatCanBeInputsForFieldQueryVariables,
  GetUsersQueryInput,
  ImpersonateUserMutation,
  ImpersonateUserMutationVariables,
  InviteCitizenMutation,
  InviteCitizenMutationVariables,
  InviteStaffMutation,
  InviteStaffMutationVariables,
  MakeApplicantUserOrganizationCsvMutation,
  MakeApplicantUserOrganizationCsvMutationVariables,
  MakeUserGovWellStaffMutation,
  MakeUserGovWellStaffMutationVariables,
  ReactivateUserMutation,
  ReactivateUserMutationVariables,
  SearchUsersInput,
  SearchUsersQuery,
  SearchUsersQueryVariables,
  SetUserSignatureFileIdMutation,
  SetUserSignatureFileIdMutationVariables,
  UpdateCitizenUserMutation,
  UpdateCitizenUserMutationVariables,
  UpdateUserIsAIStaffMemberMutation,
  UpdateUserIsAIStaffMemberMutationVariables,
  UpdateUserMetadataMutation,
  UpdateUserMetadataMutationVariables,
  UpdateUserRecordTypeAccessesMutation,
  UpdateUserRecordTypeAccessesMutationVariables,
  UsersThatCanBeInputsForFieldInput,
  UsersWithAccessToModuleQuery,
  UsersWithAccessToModuleQueryVariables,
  PromoteUserToStaffMutation,
  PromoteUserToStaffMutationVariables,
  DemoteUserFromStaffMutation,
  DemoteUserFromStaffMutationVariables,
  GetMySignatureQuery,
  GetMySignatureQueryVariables,
  GetSignatureByUserUuidQuery,
  GetSignatureByUserUuidQueryVariables,
} from 'types/graphql'

import { MillisecondsInMinute } from 'src/constants'
import { useMyUser } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

const GetUserByUuid = gql`
  query GetUserByUuidQuery($uuid: String!) {
    user: userByUuid(uuid: $uuid) {
      ...UserDataFragmentWithCompany
      activeInvitationsToJoinCompanies {
        ...UserCompanyInvitationFragment
      }
      pendingRequestsToJoinCompanies {
        ...UserRequestToJoinCompanyFragment
      }
    }
  }
`
export const useUserByUuidQuery = (uuid: string | null | undefined) => {
  const { data, ...rest } = useQuery<GetUserByUuidQuery, GetUserByUuidQueryVariables>({
    queryKey: [QueryKey.User, uuid],
    queryDocument: GetUserByUuid,
    variables: { uuid: uuid || '' },
    enabled: !!uuid,
  })
  const activeInvitationsToJoinCompanies = useMemo(
    () => data?.user?.activeInvitationsToJoinCompanies ?? [],
    [data?.user?.activeInvitationsToJoinCompanies]
  )
  const companies = useMemo(() => data?.user?.companies ?? [], [data?.user?.companies])
  const pendingRequestsToJoinCompanies = useMemo(
    () => data?.user?.pendingRequestsToJoinCompanies ?? [],
    [data?.user?.pendingRequestsToJoinCompanies]
  )
  return {
    ...rest,
    activeInvitationsToJoinCompanies,
    companies,
    pendingRequestsToJoinCompanies,
    user: data?.user,
  }
}

const UpdateCitizenUser = gql`
  mutation UpdateCitizenUserMutation($id: Int!, $input: UpdateCitizenUserInput!) {
    updateCitizenUser(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateCitizenUserMutation = () => {
  return useMutation<UpdateCitizenUserMutation, UpdateCitizenUserMutationVariables>({
    mutationDocument: UpdateCitizenUser,
  })
}

const MakeCsv = gql`
  mutation MakeApplicantUserOrganizationCsvMutation($userIds: [Int!]!) {
    makeApplicantUserOrganizationCsv(userIds: $userIds)
  }
`

export const useMakeApplicantUserOrganizationCsvMutation = () => {
  return useMutation<
    MakeApplicantUserOrganizationCsvMutation,
    MakeApplicantUserOrganizationCsvMutationVariables
  >({ mutationDocument: MakeCsv })
}

const InviteCitizen = gql`
  mutation InviteCitizenMutation($input: InviteCitizenInput!) {
    inviteCitizen(input: $input) {
      ...UserDataFragmentWithCompany
    }
  }
`
export const useInviteCitizenMutation = () => {
  return useMutation<InviteCitizenMutation, InviteCitizenMutationVariables>({
    mutationDocument: InviteCitizen,
  })
}

const Deactivate = gql`
  mutation DeactivateUserMutation($id: Int!) {
    deactivateUser(id: $id) {
      id
    }
  }
`
export const useDeactivateUserMutation = () => {
  return useMutation<DeactivateUserMutation, DeactivateUserMutationVariables>({
    mutationDocument: Deactivate,
  })
}

const Reactivate = gql`
  mutation ReactivateUserMutation($id: Int!) {
    reactivateUser(id: $id) {
      id
    }
  }
`
export const useReactivateUserMutation = () => {
  return useMutation<ReactivateUserMutation, ReactivateUserMutationVariables>({
    mutationDocument: Reactivate,
  })
}

const MakeUserGovWellStaff = gql`
  mutation MakeUserGovWellStaffMutation($id: Int!) {
    makeUserGovWellStaff(id: $id) {
      id
    }
  }
`
export const useMakeUserGovWellStaffMutation = () => {
  return useMutation<MakeUserGovWellStaffMutation, MakeUserGovWellStaffMutationVariables>({
    mutationDocument: MakeUserGovWellStaff,
  })
}

const UpdateRecordTypeAccesses = gql`
  mutation UpdateUserRecordTypeAccessesMutation($input: UpdateUserRecordTypeAccessesInput!) {
    updateUserRecordTypeAccesses(input: $input)
  }
`

export const useUpdateUserRecordTypeAccessesMutation = () => {
  return useMutation<
    UpdateUserRecordTypeAccessesMutation,
    UpdateUserRecordTypeAccessesMutationVariables
  >({
    mutationDocument: UpdateRecordTypeAccesses,
  })
}

const InviteStaff = gql`
  mutation InviteStaffMutation($input: InviteStaffInput!) {
    inviteStaff(input: $input) {
      id
    }
  }
`
export const useInviteStaffMutation = () => {
  return useMutation<InviteStaffMutation, InviteStaffMutationVariables>({
    mutationDocument: InviteStaff,
  })
}

const UpdateMetadata = gql`
  mutation UpdateUserMetadataMutation($id: Int!, $input: UpdateUserMetadataInput!) {
    updateUserMetadata(id: $id, input: $input) {
      id
    }
  }
`
export const useUpdateUserMetadataMutation = () => {
  return useMutation<UpdateUserMetadataMutation, UpdateUserMetadataMutationVariables>({
    mutationDocument: UpdateMetadata,
  })
}

const Delete = gql`
  mutation DeleteUserMutation($id: Int!) {
    deleteUser(id: $id) {
      id
    }
  }
`
export const useDeleteUserMutation = () => {
  return useMutation<DeleteUserMutation, DeleteUserMutationVariables>({
    mutationDocument: Delete,
  })
}

const Impersonate = gql`
  mutation ImpersonateUserMutation($id: Int!) {
    impersonateUser(id: $id) {
      impersonateUrl
    }
  }
`
export const useImpersonateUserMutation = () => {
  return useMutation<ImpersonateUserMutation, ImpersonateUserMutationVariables>({
    mutationDocument: Impersonate,
  })
}

const SetUserSignatureFileId = gql`
  mutation SetUserSignatureFileIdMutation($signatureFileId: Int!) {
    setUserSignatureFileId(signatureFileId: $signatureFileId) {
      id
    }
  }
`
export const useSetUserSignatureFileIdMutation = () => {
  return useMutation<SetUserSignatureFileIdMutation, SetUserSignatureFileIdMutationVariables>({
    mutationDocument: SetUserSignatureFileId,
  })
}

const AdminUsers = gql`
  query AdminUsersQuery($input: GetUsersQueryInput) {
    users: usersPage(input: $input) {
      data {
        ...UserDataFragment
        uuid
        organization {
          city
        }
      }
      count
    }
  }
`

export const useAdminUsersQuery = (args: { input: GetUsersQueryInput }) => {
  const { input } = args
  return useQuery<AdminUsersQuery, AdminUsersQueryVariables>({
    placeholderData: keepPreviousData,
    queryKey: [QueryKey.AdminUsersPage, args],
    queryDocument: AdminUsers,
    variables: { input },
  })
}

const UsersWithAccessToModule = gql`
  query UsersWithAccessToModuleQuery($recordTypeId: Int!) {
    usersWithAccessToModule(recordTypeId: $recordTypeId) {
      ...UserDataFragment
    }
  }
`

export const useUsersWithAccessToModule = (
  recordTypeId: number | null | undefined,
  options?: { enabled?: boolean }
) => {
  const { data, ...rest } = useQuery<
    UsersWithAccessToModuleQuery,
    UsersWithAccessToModuleQueryVariables
  >({
    queryKey: [QueryKey.UsersWithAccessToModule, recordTypeId],
    queryDocument: UsersWithAccessToModule,
    variables: { recordTypeId: recordTypeId ?? 0 },
    enabled: options?.enabled !== false && !!recordTypeId,
  })
  const users = useMemo(() => data?.usersWithAccessToModule ?? [], [data?.usersWithAccessToModule])
  return { ...rest, users }
}

const UpdateUserIsAIStaffMember = gql`
  mutation UpdateUserIsAIStaffMemberMutation($id: Int!, $isAIStaffMember: Boolean!) {
    updateUserIsAIStaffMember(id: $id, isAIStaffMember: $isAIStaffMember) {
      id
    }
  }
`

export const useUpdateUserIsAIStaffMemberMutation = () => {
  return useMutation<UpdateUserIsAIStaffMemberMutation, UpdateUserIsAIStaffMemberMutationVariables>(
    {
      mutationDocument: UpdateUserIsAIStaffMember,
    }
  )
}

export const SearchUsers = gql`
  query SearchUsersQuery($query: String!, $input: SearchUsersInput) {
    searchUsers(query: $query, input: $input) {
      ...UserDataFragmentWithCompany
    }
  }
`
export const useSearchUsersQuery = (args: { query: string; input?: SearchUsersInput }) => {
  const { query, input } = args
  return useQuery<SearchUsersQuery, SearchUsersQueryVariables>({
    queryKey: [QueryKey.SearchUsers, query],
    queryDocument: SearchUsers,
    variables: { query, input },
    enabled: !!query,
    placeholderData: keepPreviousData,
  })
}

const GetIntercomJwt = gql`
  query GetIntercomJwtQuery {
    getIntercomJwt
  }
`

export const useGetIntercomJwt = (enabled: boolean) => {
  return useQuery<GetIntercomJwtQuery, GetIntercomJwtQueryVariables>({
    queryKey: [QueryKey.GetIntercomJwt],
    queryDocument: GetIntercomJwt,
    variables: {},
    enabled,
  })
}

const GetTestApplicant = gql`
  query GetTestApplicantQuery {
    getTestApplicant {
      ...UserDataFragmentWithCompany
    }
  }
`

export const useGetTestApplicantQuery = (enabled: boolean) => {
  const { data, ...rest } = useQuery<GetTestApplicantQuery, GetTestApplicantQueryVariables>({
    queryKey: [QueryKey.GetTestApplicant],
    queryDocument: GetTestApplicant,
    variables: {},
    enabled,
  })
  return { applicant: data?.getTestApplicant, ...rest }
}

const GetOrganizationCalendarConnections = gql`
  query GetOrganizationCalendarConnectionsQuery($organizationId: Int!) {
    users: getOrganizationCalendarConnections(organizationId: $organizationId) {
      ...UserDataFragment
    }
  }
`

export const useOrganizationCalendarConnectionsQuery = (
  organizationId: number | null | undefined
) => {
  const { data, ...rest } = useQuery<
    GetOrganizationCalendarConnectionsQuery,
    GetOrganizationCalendarConnectionsQueryVariables
  >({
    queryKey: [QueryKey.GetOrganizationCalendarConnections, organizationId],
    queryDocument: GetOrganizationCalendarConnections,
    variables: { organizationId: organizationId ?? 0 },
    enabled: !!organizationId,
  })

  const connections = useMemo(() => data?.users ?? [], [data?.users])

  return {
    ...rest,
    connections,
  }
}

const GetUsersThatCanBeInputsForField = gql`
  query GetUsersThatCanBeInputsForFieldQuery($input: UsersThatCanBeInputsForFieldInput!) {
    users: usersThatCanBeInputsForField(input: $input) {
      ...UserDataFragment
    }
  }
`
export const useUsersThatCanBeInputsForFieldQuery = (input: UsersThatCanBeInputsForFieldInput) => {
  const { data, ...rest } = useQuery<
    GetUsersThatCanBeInputsForFieldQuery,
    GetUsersThatCanBeInputsForFieldQueryVariables
  >({
    queryKey: [QueryKey.UsersThatCanBeInputsForField, input],
    queryDocument: GetUsersThatCanBeInputsForField,
    variables: { input },
  })
  const users = useMemo(() => data?.users ?? [], [data?.users])
  return {
    ...rest,
    users,
  }
}

const GetUsersByIds = gql`
  query GetUsersByIdsQuery($ids: [Int!]!) {
    users: getUsersByIds(ids: $ids) {
      ...UserDataFragment
    }
  }
`

export const useUsersByIdsQuery = (ids: number[]) => {
  const { data, ...rest } = useQuery<GetUsersByIdsQuery, GetUsersByIdsQueryVariables>({
    queryKey: [QueryKey.Users, ids],
    queryDocument: GetUsersByIds,
    variables: { ids },
    enabled: ids.length > 0,
  })
  const users = useMemo(() => data?.users ?? [], [data?.users])
  return {
    ...rest,
    users,
  }
}

const GetAIStaffMembers = gql`
  query GetAIStaffMembersQuery {
    getAIStaffMembers {
      ...UserDataFragment
    }
  }
`

export const useGetAIStaffMembersQuery = () => {
  const { data, ...rest } = useQuery<GetAIStaffMembersQuery, GetAIStaffMembersQueryVariables>({
    queryKey: [QueryKey.AIStaffMembers],
    queryDocument: GetAIStaffMembers,
    variables: {},
  })
  const users = useMemo(() => data?.getAIStaffMembers ?? [], [data?.getAIStaffMembers])
  return {
    ...rest,
    users,
  }
}

const PromoteUserToStaff = gql`
  mutation PromoteUserToStaffMutation($id: Int!, $input: PromoteUserInput!) {
    promoteUserToStaff(id: $id, input: $input) {
      id
    }
  }
`
export const usePromoteUserToStaffMutation = () => {
  return useMutation<PromoteUserToStaffMutation, PromoteUserToStaffMutationVariables>({
    mutationDocument: PromoteUserToStaff,
  })
}

const DemoteUserFromStaff = gql`
  mutation DemoteUserFromStaffMutation($id: Int!) {
    demoteUserFromStaff(id: $id) {
      id
    }
  }
`
export const useDemoteUserFromStaffMutation = () => {
  return useMutation<DemoteUserFromStaffMutation, DemoteUserFromStaffMutationVariables>({
    mutationDocument: DemoteUserFromStaff,
  })
}

const GetMySignature = gql`
  query GetMySignatureQuery {
    me {
      signatureFile {
        ...FileFragment
      }
    }
  }
`

export const useGetMySignatureQuery = () => {
  const me = useMyUser()
  const { data, ...rest } = useQuery<GetMySignatureQuery, GetMySignatureQueryVariables>({
    queryKey: [QueryKey.MySignature, me?.id],
    queryDocument: GetMySignature,
    enabled: !!me,
    variables: {},
    staleTime: 5 * MillisecondsInMinute,
  })
  return {
    ...rest,
    signatureFile: data?.me?.signatureFile,
  }
}

const GetSignatureByUserUuid = gql`
  query GetSignatureByUserUuidQuery($userUuid: String!) {
    userByUuid(uuid: $userUuid) {
      signatureFile {
        ...FileFragment
      }
    }
  }
`
export const useGetSignatureByUserUuidQuery = (userUuid: string | null | undefined) => {
  const { data, ...rest } = useQuery<
    GetSignatureByUserUuidQuery,
    GetSignatureByUserUuidQueryVariables
  >({
    queryKey: [QueryKey.SignatureByUserUuid, userUuid],
    queryDocument: GetSignatureByUserUuid,
    variables: { userUuid: userUuid || '' },
    enabled: !!userUuid,
  })
  return {
    ...rest,
    signatureFile: data?.userByUuid?.signatureFile,
  }
}
